import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { apiCall } from '../../../../utils/apiClient';
import CreateAdjustmentModal, {
  NewAdjustmentPayload,
} from './CreateAdjustmentModal';

/* ================= TYPES ================= */

type AdjustmentType = NewAdjustmentPayload["type"];

export interface BookingAdjustment {
  id: string;
  type: AdjustmentType | string;
  related_to: string;
  amount: number;
  reason: string;
  status: string;
  created_at: string;
  notes?: string;
}

interface AdjustmentsTabProps {
  bookingId?: number | string;
  adjustments?: BookingAdjustment[];
  hideStatus?: boolean;
}

type ApiAdjustment = {
  id?: string | number;
  adjustment_type?: string;
  type?: string;
  label?: string;
  amount?: number | string;
  is_addition?: number | boolean | string;
  is_taxable?: number | boolean | string;
  status?: string;
  created_at?: string;
  createdAt?: string;
  created_by?: string;
  related_to?: string;
  reason?: string;
  notes?: string;
};

const toBool = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.toLowerCase().trim();
    if (normalized === '1' || normalized === 'true' || normalized === 'yes') {
      return true;
    }
    if (normalized === '0' || normalized === 'false' || normalized === 'no') {
      return false;
    }
  }
  return undefined;
};

const toNumber = (value: unknown, fallback = 0): number => {
  if (value === null || value === undefined || value === '') return fallback;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toTitleCase = (value: string) => {
  return value
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const normalizeApiAdjustment = (item: ApiAdjustment): BookingAdjustment => {
  const rawAmount = toNumber(item.amount);
  const isAddition = toBool(item.is_addition);
  const normalizedAmount =
    isAddition === undefined
      ? rawAmount
      : isAddition
      ? Math.abs(rawAmount)
      : -Math.abs(rawAmount);

  const typeLabel = item.adjustment_type
    ? toTitleCase(item.adjustment_type)
    : item.type
    ? toTitleCase(String(item.type))
    : 'Adjustment';

  const statusLabel = item.status ? toTitleCase(item.status) : 'Pending';

  return {
    id: String(item.id ?? `ADJ-${Date.now()}`),
    type: typeLabel,
    related_to: item.related_to || 'Booking',
    amount: normalizedAmount,
    reason: item.label || item.reason || '-',
    status: statusLabel,
    created_at:
      item.created_at || item.createdAt || new Date().toISOString(),
    notes: item.notes,
  };
};

const getAdjustmentKey = (item: BookingAdjustment): string => {
  if (item.id !== undefined && item.id !== null && String(item.id).trim() !== '') {
    return `id:${String(item.id)}`;
  }

  return `sig:${String(item.type)}|${String(item.related_to)}|${Number(item.amount || 0)}|${String(
    item.created_at || ''
  )}|${String(item.reason || '')}`;
};

const mergeUniqueAdjustments = (
  primary: BookingAdjustment[],
  secondary: BookingAdjustment[]
): BookingAdjustment[] => {
  const unique = new Map<string, BookingAdjustment>();

  [...primary, ...secondary].forEach((item) => {
    const key = getAdjustmentKey(item);
    if (!unique.has(key)) {
      unique.set(key, item);
    }
  });

  return Array.from(unique.values());
};

/* ================= COMPONENT ================= */

const AdjustmentsTab: React.FC<AdjustmentsTabProps> = ({
  bookingId,
  adjustments = [],
  hideStatus = false,
}) => {
  const [localAdjustments, setLocalAdjustments] =
    useState<BookingAdjustment[]>([...adjustments]);

  const [apiAdjustments, setApiAdjustments] = useState<
    BookingAdjustment[]
  >([]);

  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAdjustments = async () => {
    if (!bookingId) return;

    setLoading(true);
    setError(null);

    try {
      const res = await apiCall({
        endpoint: `booking/adjustments/${bookingId}`,
        method: 'GET',
      });

      if (res && (res.status === 200 || res.success === true)) {
        const raw =
          (Array.isArray(res.data) && res.data) ||
          (Array.isArray((res.data as any)?.data) &&
            (res.data as any).data) ||
          (Array.isArray((res.data as any)?.adjustments) &&
            (res.data as any).adjustments) ||
          [];

        const normalized = raw.map((item: ApiAdjustment) =>
          normalizeApiAdjustment(item)
        );
        setApiAdjustments(normalized);
      } else {
        setError(res?.message || 'Failed to load adjustments');
      }
    } catch (err) {
      console.error('Error fetching adjustments:', err);
      setError('Failed to load adjustments');
    } finally {
      setLoading(false);
    }

  };

  useEffect(() => {
    if (bookingId) {
      fetchAdjustments();
      return;
    }

    setLocalAdjustments([...adjustments]);
  }, [bookingId, adjustments]);

  const displayedAdjustments = useMemo(() => {
    if (bookingId) {
      if (apiAdjustments.length > 0 || adjustments.length > 0) {
        return mergeUniqueAdjustments(apiAdjustments, adjustments);
      }
      return [];
    }

    return localAdjustments;
  }, [bookingId, apiAdjustments, adjustments, localAdjustments]);

  const totalImpact = displayedAdjustments.reduce(
    (sum, item) => sum + item.amount,
    0
  );

  const handleAddAdjustment = async (
    data: NewAdjustmentPayload
  ): Promise<boolean> => {
    if (bookingId) {
      const isAddition =
        data.type === 'Extra Charge' || data.type === 'Penalty';
      const payload = {
        adjustment_type: data.type
          .toLowerCase()
          .replace(/\s+/g, '_'),
        label: data.reason,
        amount: Math.abs(data.amount),
        is_addition: isAddition ? 1 : 0,
        is_taxable: 0,
        created_by: 'admin',
      };

      setSubmitting(true);
      try {
        const res = await apiCall({
          endpoint: `booking/adjustments/${bookingId}`,
          method: 'POST',
          data: payload,
        });

        if (
          res &&
          (res.status === 200 ||
            res.status === 201 ||
            res.success === true)
        ) {
          toast.success('Adjustment added successfully');
          await fetchAdjustments();
          return true;
        }

        toast.error(res?.message || 'Failed to add adjustment');
        return false;
      } catch (err) {
        console.error('Error adding adjustment:', err);
        toast.error('Failed to add adjustment');
        return false;
      } finally {
        setSubmitting(false);
      }
    }

    const newAdjustment: BookingAdjustment = {
      id: `ADJ-${Date.now()}`,
      type: data.type,
      related_to: data.related_to,
      amount: data.amount,
      reason: data.reason,
      status: 'Pending',
      created_at: new Date().toISOString(),
      notes: data.notes,
    };

    setLocalAdjustments((prev) => [newAdjustment, ...prev]);
    return true;
  };

  return (
    <div className="mb-6">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-800">
          Price Adjustments
        </h3>

        <button
          className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm"
          onClick={() => setShowModal(true)}
        >
          + Create Adjustment
        </button>
      </div>

      {/* SUMMARY */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
        <div className="flex justify-between">
          <div>
            <p className="text-sm text-gray-600">Net Impact</p>
            <p
              className={`text-2xl font-bold ${totalImpact < 0 ? 'text-red-600' : 'text-green-600'
                }`}
            >
              ₹{totalImpact.toFixed(2)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Adjustments</p>
            <p className="text-2xl font-bold">
              {displayedAdjustments.length}
            </p>
          </div>
        </div>
      </div>

      {/* TABLE */}
      {displayedAdjustments.length > 0 ? (
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left">Type</th>
                <th className="px-4 py-2 text-left">Related To</th>
                <th className="px-4 py-2 text-left">Amount</th>
                <th className="px-4 py-2 text-left">Reason</th>
                {!hideStatus && (
                  <th className="px-4 py-2 text-left">Status</th>
                )}
                <th className="px-4 py-2 text-left">Date</th>
              </tr>
            </thead>
            <tbody>
              {displayedAdjustments.map((item, index) => (
                <tr
                  key={item.id}
                  className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                >
                  <td className="px-4 py-2 font-medium">
                    {item.type}
                  </td>
                  <td className="px-4 py-2">
                    {item.related_to}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${item.amount < 0
                          ? 'bg-red-100 text-red-800'
                          : 'bg-green-100 text-green-800'
                        }`}
                    >
                      ₹{item.amount.toFixed(2)}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    {item.reason}
                  </td>
                  {!hideStatus && (
                    <td className="px-4 py-2 text-xs">
                      {item.status}
                    </td>
                  )}
                  <td className="px-4 py-2 text-xs">
                    {new Date(item.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center p-6 text-gray-500">
          {loading ? 'Loading adjustments...' : 'No adjustments added yet.'}
        </div>
      )}

      {error && (
        <div className="mt-3 text-sm text-red-600">
          {error}
        </div>
      )}

      {/* MODAL */}
      {showModal && (
        <CreateAdjustmentModal
          onClose={() => setShowModal(false)}
          onSubmit={handleAddAdjustment}
          isSubmitting={submitting}
        />
      )}
    </div>
  );
};

export default AdjustmentsTab;
